import { Fragment, useEffect, useMemo, useRef, useState } from 'react'
import { Link } from 'react-router-dom'
import AppDateInput from '../../components/AppDateInput'
import AppDropdown from '../../components/AppDropdown'
import AppPagination from '../../components/AppPagination'
import QuickExportButton from '../../components/QuickExportButton'
import { useExportJob } from '../../hooks/useExportJob'
import { useSupportTicketManagement } from './hooks/useSupportTicketManagement'
import AssignAgentModal from './components/AssignAgentModal'
import PlatformStaffManagementModal from './components/PlatformStaffManagementModal'
import BreathingBadgeDot from './components/BreathingBadgeDot'

const TAB_OPTIONS = ['OPEN', 'IN_PROGRESS', 'CLOSED', 'ALL']
const PRIORITY_OPTIONS = [
  { value: '', label: 'All Priorities' },
  { value: 'HIGH', label: 'High' },
  { value: 'MEDIUM', label: 'Medium' },
  { value: 'LOW', label: 'Low' },
]
const CATEGORY_OPTIONS = [
  { value: '', label: 'All Categories' },
  { value: 'ISSUE', label: 'Issue' },
  { value: 'QUESTION', label: 'Question' },
  { value: 'SUGGESTION', label: 'Suggestion' },
  { value: 'FEATURE_REQUEST', label: 'Feature Request' },
]

function statusClass(status) {
  const normalized = String(status || '').toUpperCase()
  if (normalized === 'CLOSED') return 'bg-emerald-100 text-emerald-800'
  if (normalized === 'IN_PROGRESS') return 'bg-amber-100 text-amber-800'
  return 'bg-blue-100 text-blue-800'
}

function tabLabel(value) {
  if (value === 'IN_PROGRESS') return 'In Progress'
  if (value === 'ALL') return 'All'
  return value.charAt(0) + value.slice(1).toLowerCase()
}

function CommentBubble({ comment, isOwn, formatDateTime }) {
  return (
    <div className={`flex gap-3 ${isOwn ? 'flex-row-reverse' : 'flex-row'}`}>
      {/* Avatar */}
      <div
        className={`flex h-8 w-8 flex-shrink-0 items-center justify-center rounded-full text-xs font-bold ${
          comment.isStaff ? 'bg-primary text-on-primary' : 'bg-surface-container text-on-surface-variant'
        }`}
      >
        {String(comment.userName || 'U').charAt(0).toUpperCase()}
      </div>

      {/* Bubble */}
      <div className={`max-w-[70%] ${isOwn ? 'items-end' : 'items-start'} flex flex-col gap-0.5`}>
        <div className={`flex items-center gap-2 text-xs text-on-surface-variant ${isOwn ? 'flex-row-reverse' : 'flex-row'}`}>
          <span className="font-semibold">{comment.userName}</span>
          {comment.isStaff ? (
            <span className="rounded-sm bg-primary/10 px-1.5 py-0.5 text-[10px] font-bold uppercase tracking-wider text-primary">
              Staff
            </span>
          ) : null}
          <span className="text-[11px] opacity-60">{formatDateTime(comment.createdAt)}</span>
        </div>
        <div
          className={`rounded-2xl px-3.5 py-2.5 text-sm leading-relaxed break-words whitespace-pre-wrap ${
            isOwn
              ? 'rounded-tr-sm bg-primary text-on-primary'
              : 'rounded-tl-sm bg-surface-container text-on-surface'
          }`}
        >
          {comment.comment}
        </div>
      </div>
    </div>
  )
}

export function ConversationPanel({ row, state, actions }) {
  const comments = state.commentsByTicket[row.id] || []
  const isLoading = Boolean(state.commentsLoading[row.id])
  const inputText = state.newCommentText[row.id] || ''
  const isPosting = Boolean(state.isPostingComment[row.id])
  const canComment = actions.canCommentOnTicket(row)
  const [isReopening, setIsReopening] = useState(false)
  const composerRef = useRef(null)
  const feedRef = useRef(null)
  const isClosed = row.status === 'CLOSED'
  const isOwner = String(row.requesterId) === String(state.currentUserId)
    && Number(row.tenantId) === Number(state.currentTenantId || 0)
  const latestClosure = comments.filter((comment) => comment.eventType === 'CLOSED').at(-1)
  const canReopen = isClosed && isOwner && canComment

  useEffect(() => {
    if (!isClosed) setIsReopening(false)
  }, [isClosed])

  useEffect(() => {
    if (isReopening) composerRef.current?.focus()
  }, [isReopening])

  useEffect(() => {
    if (feedRef.current) feedRef.current.scrollTop = feedRef.current.scrollHeight
  }, [comments.length, isReopening])

  async function sendMessage() {
    if (!canComment || isPosting || !inputText.trim()) return
    const sent = await actions.postComment(row.id, { reopenTicket: isReopening && canReopen })
    if (sent) setIsReopening(false)
  }

  function closureNotice(comment) {
    return (
      <div key={comment.id} className="rounded-xl border border-emerald-200 bg-emerald-50 px-4 py-3 text-center text-sm text-emerald-900">
        <p>{comment.comment}</p>
        {canReopen && (!latestClosure || latestClosure.id === comment.id) ? (
          <p className="mt-1">
            Need more help?{' '}
            <button
              type="button"
              onClick={() => setIsReopening(true)}
              aria-expanded={isReopening}
              aria-controls={`ticket-composer-${row.id}`}
              className="font-semibold underline underline-offset-2 hover:text-primary focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 cursor-pointer"
            >
              Tell us what still needs attention.
            </button>
          </p>
        ) : null}
      </div>
    )
  }

  function handleKeyDown(event) {
    if (event.key === 'Enter' && (event.ctrlKey || event.metaKey)) {
      event.preventDefault()
      sendMessage()
    }
  }

  return (
    <div className="mt-4 xl:mt-0 overflow-hidden rounded-xl border border-outline-variant/20 bg-surface-container-lowest">
      {/* Header */}
      <div className="flex items-center gap-2 border-b border-outline-variant/20 px-4 py-2.5">
        <span className="material-symbols-outlined text-sm text-on-surface-variant">forum</span>
        <h4 className="text-sm font-bold text-on-surface">Conversation</h4>
        <span className="ml-auto rounded-full bg-surface-container px-2 py-0.5 text-[11px] font-semibold text-on-surface-variant">
          {comments.length}
        </span>
      </div>

      {/* Messages */}
      <div ref={feedRef} className="flex max-h-[320px] flex-col gap-3 overflow-y-auto p-4" id={`comment-feed-${row.id}`} role="log" aria-label="Ticket conversation" aria-live="polite">
        {isLoading ? (
          <p className="text-center text-sm text-on-surface-variant">Loading conversation...</p>
        ) : comments.length === 0 && !isClosed ? (
          <div className="flex flex-col items-center gap-2 py-4 text-center">
            <span className="material-symbols-outlined text-3xl text-on-surface-variant/40">forum</span>
            <p className="text-sm text-on-surface-variant">No messages yet. Start the conversation below.</p>
          </div>
        ) : (
          comments.map((comment) => {
            if (comment.eventType === 'CLOSED') return closureNotice(comment)
            if (comment.eventType === 'REOPENED') {
              return (
                <div key={comment.id} className="self-center rounded-lg border border-outline-variant/30 bg-surface-container-low px-4 py-2 text-center text-xs font-medium text-on-surface-variant">
                  {comment.comment}
                </div>
              )
            }
            const isOwn = String(comment.userId) === String(state.currentUserId)
            return (
              <CommentBubble
                key={comment.id}
                comment={comment}
                isOwn={isOwn}
                formatDateTime={actions.formatDateTime}
              />
            )
          })
        )}
        {!isLoading && isClosed && !latestClosure ? closureNotice({
          id: `closed-${row.id}`,
          comment: 'Your ticket has been resolved and closed. Please test the update.',
        }) : null}
      </div>

      {/* Composer */}
      <div id={`ticket-composer-${row.id}`} className="border-t border-outline-variant/20 p-3">
        {isReopening && canReopen ? (
          <div className="mb-2 flex items-center justify-between gap-2 text-xs text-on-surface-variant">
            <p>Tell us what needs attention. Sending feedback will reopen this ticket.</p>
            <button type="button" disabled={isPosting} onClick={() => setIsReopening(false)} className="font-semibold text-primary underline cursor-pointer disabled:opacity-40">Cancel</button>
          </div>
        ) : null}
        {canComment && (!canReopen || isReopening) ? (
          <div className="flex items-center gap-2 rounded-xl border border-outline-variant/30 bg-surface-container-low p-1.5 focus-within:border-primary focus-within:ring-1 focus-within:ring-primary">
            <textarea
              ref={composerRef}
              value={inputText}
              disabled={isPosting}
              maxLength={5000}
              aria-label={isReopening ? 'Feedback to reopen ticket' : 'Message'}
              onChange={(event) => actions.setNewCommentText(row.id, event.target.value)}
              onKeyDown={handleKeyDown}
              rows={isReopening ? 3 : 1}
              placeholder={isReopening ? 'What still needs attention? (Ctrl+Enter to send)' : 'Write a message… (Ctrl+Enter)'}
              className="flex-1 resize-none bg-transparent px-2.5 py-1.5 text-sm text-on-surface placeholder:text-on-surface-variant/50 focus:outline-none outline-none"
            />
            <button
              type="button"
              onClick={sendMessage}
              disabled={isPosting || !inputText.trim()}
              className="flex h-8 w-8 flex-shrink-0 items-center justify-center rounded-lg bg-primary text-on-primary hover:bg-primary/95 disabled:opacity-40 transition-colors cursor-pointer"
              aria-label={isReopening ? 'Send feedback and reopen ticket' : 'Send message'}
            >
              {isPosting ? (
                <span className="material-symbols-outlined animate-spin text-sm">autorenew</span>
              ) : (
                <span className="material-symbols-outlined text-sm">send</span>
              )}
            </button>
          </div>
        ) : canReopen ? (
          <p className="text-center text-xs text-on-surface-variant">Use the link above to send feedback and reopen your ticket.</p>
        ) : (
          <div className="rounded-xl border border-outline-variant/20 bg-surface-container-low/50 px-3 py-2 text-xs text-on-surface-variant/70 italic text-center">
            Only the assigned platform agent, Super Admin, or the ticket requester can reply.
          </div>
        )}
      </div>
    </div>
  )
}

export default function SupportTicketManagementScreen() {
  const { state, actions } = useSupportTicketManagement()
  const { triggerExport, isExporting } = useExportJob({
    module: 'support_tickets',
    fileFormat: 'XLSX',
  })

  const handleExport = () => {
    triggerExport({
      status: state.tab === 'ALL' ? undefined : state.tab,
      priority: state.priority || undefined,
      category: state.category || undefined,
      tenantId: state.tenantId || undefined,
      assignedAgentId: state.assignedToMeOnly ? state.currentUserId : undefined,
      dateFrom: state.dateFrom || undefined,
      dateTo: state.dateTo || undefined,
    })
  }

  const tabsWithIndicator = state.tabIndicatorSet ?? new Set()

  const tenantOptions = useMemo(() => {
    return [
      { value: '', label: 'All Customer Tenants' },
      ...(state.tenants || []).map((t) => ({
        value: String(t.id),
        label: t.name || t.code || `Tenant #${t.id}`,
      })),
    ]
  }, [state.tenants])

  const tableHeaders = useMemo(() => {
    if (state.isStaff) {
      return ['Ticket No.', 'Tenant', 'Subject', 'Date', 'Priority', 'Status', 'Assigned Agent', 'TAT', 'Actions']
    }
    return ['Ticket No.', 'Subject', 'Date', 'Priority', 'Status', 'TAT', 'Actions']
  }, [state.isStaff])

  const colSpanCount = tableHeaders.length

  return (
    <div className="mx-auto w-full max-w-[1450px] pb-10">
      <header className="mb-6 flex flex-col gap-4 md:flex-row md:items-end md:justify-between">
        <div>
          <div className="flex items-center gap-3">
            <h1 className="text-4xl font-extrabold tracking-tight text-on-surface md:text-5xl">
              Support Ticket Management
            </h1>
            {state.isSuperAdmin && (
              <span className="rounded-full bg-primary/10 border border-primary/20 px-3 py-1 text-xs font-bold text-primary">
                Super Admin
              </span>
            )}
            {state.isPlatformAgent && (
              <span className="rounded-full bg-secondary-container px-3 py-1 text-xs font-bold text-on-secondary-container">
                Platform Agent
              </span>
            )}
          </div>
          <p className="mt-2 text-sm text-on-surface-variant">
            {state.isStaff
              ? 'Cross-tenant platform support desk for managing and resolving customer tickets.'
              : 'Bridge-native support tickets for product queries, issues, and suggestions.'}
          </p>
        </div>
        <div className="flex items-center gap-3">
          {state.isSuperAdmin && (
            <button
              type="button"
              onClick={actions.openStaffModal}
              className="rounded-xl border border-primary/30 bg-primary/10 px-4 py-2 text-xs font-bold text-primary hover:bg-primary/20 transition-all cursor-pointer flex items-center gap-1.5 shadow-2xs"
            >
              <span className="material-symbols-outlined text-sm">shield_person</span>
              Platform Staff
            </button>
          )}
          <QuickExportButton onClick={handleExport} loading={isExporting} />
        </div>
      </header>

      {/* Status tabs */}
      <section className="mb-5 flex flex-wrap gap-2">
        {TAB_OPTIONS.map((tabValue) => (
          <button
            key={tabValue}
            type="button"
            onClick={() => actions.setTab(tabValue)}
            className={`relative rounded-md px-4 py-2 text-sm font-bold transition-colors cursor-pointer ${
              state.tab === tabValue
                ? 'bg-primary text-on-primary shadow-sm'
                : 'bg-surface-container text-on-surface-variant hover:bg-surface-container-high'
            }`}
          >
            {tabLabel(tabValue)}
            {tabsWithIndicator.has(tabValue) ? (
              <BreathingBadgeDot color="rose" className="absolute -right-1 -top-1" />
            ) : null}
          </button>
        ))}
      </section>

      {/* Filter Section (2-Tier De-congested Layout) */}
      <section className="mb-6 rounded-2xl border border-outline-variant/20 bg-surface-container-low p-4 shadow-2xs">
        {/* Tier 1: Main Filter Controls */}
        <div className={`grid grid-cols-1 gap-3 sm:grid-cols-2 ${state.isStaff ? 'xl:grid-cols-5' : 'xl:grid-cols-4'}`}>
          {state.isStaff && (
            <div>
              <label className="block text-[10px] font-bold uppercase tracking-wider text-on-surface-variant mb-1">
                Tenant Company
              </label>
              <AppDropdown
                value={state.tenantId}
                onChange={actions.setTenantId}
                options={tenantOptions}
              />
            </div>
          )}

          <div>
            <label className="block text-[10px] font-bold uppercase tracking-wider text-on-surface-variant mb-1">
              Category
            </label>
            <AppDropdown value={state.category} onChange={actions.setCategory} options={CATEGORY_OPTIONS} />
          </div>

          <div>
            <label className="block text-[10px] font-bold uppercase tracking-wider text-on-surface-variant mb-1">
              Priority
            </label>
            <AppDropdown value={state.priority} onChange={actions.setPriority} options={PRIORITY_OPTIONS} />
          </div>

          <div>
            <label className="block text-[10px] font-bold uppercase tracking-wider text-on-surface-variant mb-1">
              Created From
            </label>
            <AppDateInput value={state.dateFrom} onChange={actions.setDateFrom} />
          </div>

          <div>
            <label className="block text-[10px] font-bold uppercase tracking-wider text-on-surface-variant mb-1">
              Created To
            </label>
            <AppDateInput value={state.dateTo} onChange={actions.setDateTo} />
          </div>
        </div>

        {/* Tier 2: Quick Filter Toggles & Ticket Counter */}
        <div className="mt-3.5 pt-3 border-t border-outline-variant/15 flex flex-wrap items-center justify-between gap-3">
          {/* Left: Quick Toggles */}
          <div className="flex items-center gap-2 flex-wrap">
            {/* Assigned to Me toggle (Platform Staff only) */}
            {state.isStaff && (
              <button
                type="button"
                onClick={() => actions.setAssignedToMeOnly(!state.assignedToMeOnly)}
                className={`rounded-xl px-3.5 py-1.5 text-xs font-bold transition-all cursor-pointer border flex items-center gap-1.5 shadow-2xs ${
                  state.assignedToMeOnly
                    ? 'bg-primary text-on-primary border-primary shadow-xs'
                    : 'bg-surface-container-lowest text-on-surface-variant border-outline-variant/30 hover:bg-surface-container-high'
                }`}
              >
                <span className="material-symbols-outlined text-sm">
                  {state.assignedToMeOnly ? 'check_circle' : 'person'}
                </span>
                Assigned to Me
              </button>
            )}

            {/* Unseen Conversation Toggle with Breathing Dot */}
            <button
              type="button"
              onClick={() => actions.setHasUnseenOnly(!state.hasUnseenOnly)}
              className={`rounded-xl px-3.5 py-1.5 text-xs font-bold transition-all cursor-pointer border flex items-center gap-2 shadow-2xs ${
                state.hasUnseenOnly
                  ? 'bg-rose-50 text-rose-800 border-rose-300 ring-1 ring-rose-300 shadow-xs'
                  : 'bg-surface-container-lowest text-on-surface-variant border-outline-variant/30 hover:bg-surface-container-high'
              }`}
            >
              <BreathingBadgeDot color="rose" />
              <span>Unseen Activity</span>
            </button>

            {/* Reset Filters button */}
            {state.isFilterActive && (
              <button
                type="button"
                onClick={actions.resetFilters}
                className="rounded-xl px-2.5 py-1.5 text-xs font-semibold text-on-surface-variant hover:text-error hover:bg-error-container/20 transition-colors cursor-pointer flex items-center gap-1"
                title="Clear all active filters"
              >
                <span className="material-symbols-outlined text-sm">close</span>
                Reset Filters
              </button>
            )}
          </div>

          {/* Right: Clean, spacious ticket count display */}
          <div className="flex items-center gap-1.5 px-3 py-1.5 rounded-xl border border-outline-variant/20 bg-surface-container-lowest text-xs font-bold text-on-surface-variant shadow-2xs">
            <span className="material-symbols-outlined text-sm text-primary">confirmation_number</span>
            <span>Showing <strong className="text-on-surface font-extrabold">{state.rows.length}</strong> of <strong className="text-on-surface font-extrabold">{state.totalCount}</strong> tickets</span>
          </div>
        </div>
      </section>

      {state.error ? (
        <div className="mb-3 rounded-xl border border-error/30 bg-error-container px-4 py-3 text-sm text-error">
          {state.error}
        </div>
      ) : null}
      {state.success ? (
        <div className="mb-3 rounded-xl border border-emerald-300 bg-emerald-50 px-4 py-3 text-sm text-emerald-700">
          {state.success}
        </div>
      ) : null}

      {/* Table section */}
      <section className="overflow-hidden rounded-2xl border border-outline-variant/20 bg-surface-container-lowest">
        <div className="overflow-x-auto app-hide-scrollbar">
          <table className="w-full min-w-[1000px]">
            <thead className="bg-surface-container">
              <tr>
                {tableHeaders.map((header) => (
                  <th
                    key={header}
                    className={`px-3 py-3 text-left text-xs font-bold uppercase tracking-[0.14em] text-on-surface-variant ${
                      header === 'Actions' ? 'w-[140px]' : ''
                    }`}
                  >
                    {header}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {state.isLoading ? (
                <tr>
                  <td colSpan={colSpanCount} className="px-4 py-12 text-center text-sm text-on-surface-variant">
                    Loading support tickets...
                  </td>
                </tr>
              ) : state.rows.length === 0 ? (
                <tr>
                  <td colSpan={colSpanCount} className="px-4 py-12 text-center text-sm text-on-surface-variant">
                    No tickets found for selected filters.
                  </td>
                </tr>
              ) : (
                state.rows.map((row) => {
                  const isExpanded =
                    Number(state.selectedTicket?.id || 0) > 0 &&
                    Number(state.selectedTicket?.id || 0) === Number(row.id || 0)

                  // Permission flags
                  const canAssign = actions.canAssignTicket(row)
                  const canEditStatus = actions.canEditTicketStatus(row)
                  const canEdit =
                    state.isSuperAdmin ||
                    (String(row.requesterId) === String(state.currentUserId) && row.status !== 'CLOSED')

                  return (
                    <Fragment key={`ticket-${row.id || row.ticketNo}`}>
                      <tr className="border-t border-surface-container hover:bg-surface-container-low transition-colors">
                        <td className="px-3 py-3 text-sm font-bold text-primary">{row.ticketNo}</td>

                        {/* Tenant column for platform staff */}
                        {state.isStaff && (
                          <td className="px-3 py-3 text-xs font-semibold text-on-surface-variant">
                            {row.tenantName}
                          </td>
                        )}

                        <td className="px-3 py-3 text-sm text-on-surface font-medium">{row.subject}</td>
                        <td className="px-3 py-3 text-sm text-on-surface">{row.createdOn}</td>
                        <td className="px-3 py-3 text-sm text-on-surface">{row.priority}</td>
                        <td className="px-3 py-3">
                          <span
                            className={`rounded-full px-2.5 py-1 text-[10px] font-bold uppercase tracking-[0.12em] ${statusClass(
                              row.status,
                            )}`}
                          >
                            {row.statusLabel}
                          </span>
                        </td>

                        {/* Assigned agent column for platform staff */}
                        {state.isStaff && (
                          <td className="px-3 py-3 text-xs text-on-surface">
                            {row.assignedAgentEmail ? (
                              <span className="inline-flex items-center gap-1 rounded-md bg-secondary-container/40 px-2 py-0.5 text-xs font-medium text-on-surface">
                                <span className="material-symbols-outlined text-xs text-secondary">support_agent</span>
                                {row.assignedAgentEmail}
                              </span>
                            ) : (
                              <span className="text-[11px] text-on-surface-variant/60 italic">Unassigned</span>
                            )}
                          </td>
                        )}

                        <td className="px-3 py-3 text-sm text-on-surface">{row.tat}</td>

                        {/* Actions column */}
                        <td className="px-0 py-3">
                          <div className="flex flex-col gap-2 w-[140px]">
                            {/* View / Hide button */}
                            <button
                              type="button"
                              onClick={() => (isExpanded ? actions.closeDetails() : actions.openDetails(row))}
                              className="relative inline-flex items-center justify-center gap-1 rounded-md border border-outline-variant/30 px-2 py-1 text-xs font-semibold text-on-surface hover:bg-surface-container-low transition-colors cursor-pointer"
                            >
                              {!isExpanded && state.indicatorMap && state.indicatorMap.has(row.id) ? (
                                <BreathingBadgeDot color="rose" className="absolute -right-1 -top-1" />
                              ) : null}
                              <span className="material-symbols-outlined text-sm">
                                {isExpanded ? 'expand_less' : 'expand_more'}
                              </span>
                              {isExpanded ? 'Hide' : 'View'}
                            </button>

                            {/* Super Admin: Assign Agent button */}
                            {canAssign && (
                              <button
                                type="button"
                                onClick={() => actions.openAssignModal(row)}
                                className="inline-flex items-center justify-center gap-1 rounded-md border border-primary/40 bg-primary/5 px-2 py-1 text-xs font-bold text-primary hover:bg-primary/10 transition-colors cursor-pointer"
                              >
                                <span className="material-symbols-outlined text-sm">person_add</span>
                                {row.assignedAgentId ? 'Reassign' : 'Assign'}
                              </button>
                            )}

                            {/* Edit button */}
                            {canEdit && (
                              <Link
                                to={`/support/tickets/entry?id=${row.id}`}
                                className="inline-flex items-center justify-center gap-1 rounded-md border border-outline-variant/30 px-2 py-1 text-xs font-semibold text-on-surface hover:bg-surface-container-low transition-colors"
                              >
                                <span className="material-symbols-outlined text-sm">edit</span>
                                Edit
                              </Link>
                            )}

                            {/* Status update (Super Admin or Assigned Agent) */}
                            {canEditStatus ? (
                              <>
                                <AppDropdown
                                  value={state.rowEdits[row.id]?.status || row.status}
                                  onChange={(value) => actions.updateRowEdit(row.id, 'status', String(value || row.status))}
                                  options={state.statusEditOptions}
                                  triggerClassName="h-8 rounded-md border border-outline-variant/30 bg-surface-container-lowest px-2 text-xs"
                                />
                                <button
                                  type="button"
                                  disabled={state.isSaving}
                                  onClick={() => actions.saveRowUpdate(row.id)}
                                  className="rounded-md bg-primary px-2 py-1 text-xs font-bold text-on-primary hover:bg-primary/95 disabled:opacity-60 transition-colors cursor-pointer"
                                >
                                  Update Status
                                </button>
                              </>
                            ) : state.isStaff ? (
                              <span className="text-[11px] text-on-surface-variant/60 italic text-center">
                                View only
                              </span>
                            ) : null}
                          </div>
                        </td>
                      </tr>

                      {/* Expanded detail + conversation */}
                      {isExpanded ? (
                        <tr className="border-t border-surface-container bg-surface-container-low">
                          <td colSpan={colSpanCount} className="px-4 py-4">
                            <div className="w-full grid grid-cols-1 gap-4 xl:grid-cols-2 items-start">
                              {/* Ticket details */}
                              <div>
                                <div className="flex flex-col gap-3 text-sm">
                                  {state.isStaff && (
                                    <p>
                                      <span className="font-semibold text-on-surface">Tenant:</span>{' '}
                                      <span className="text-on-surface-variant">{row.tenantName}</span>
                                    </p>
                                  )}
                                  {state.isStaff && row.assignedAgentEmail && (
                                    <p>
                                      <span className="font-semibold text-on-surface">Assigned Agent:</span>{' '}
                                      <span className="text-primary font-medium">{row.assignedAgentEmail}</span>
                                    </p>
                                  )}
                                  <p>
                                    <span className="font-semibold text-on-surface">Subject:</span>{' '}
                                    <span className="text-on-surface-variant">{row.subject}</span>
                                  </p>
                                  <p>
                                    <span className="font-semibold text-on-surface">Requester:</span>{' '}
                                    <span className="text-on-surface-variant">{row.requester}</span>
                                  </p>
                                  <p>
                                    <span className="font-semibold text-on-surface">Status:</span>{' '}
                                    <span className="text-on-surface-variant">{row.statusLabel}</span>
                                  </p>
                                  <div>
                                    <p className="mb-1 font-semibold text-on-surface">Description:</p>
                                    <p className="whitespace-pre-wrap text-sm text-on-surface-variant">
                                      {row.description || '-'}
                                    </p>
                                  </div>
                                  <p>
                                    <span className="font-semibold text-on-surface">Category:</span>{' '}
                                    <span className="text-on-surface-variant">{row.category}</span>
                                  </p>
                                  <p>
                                    <span className="font-semibold text-on-surface">Priority:</span>{' '}
                                    <span className="text-on-surface-variant">{row.priority}</span>
                                  </p>
                                  <p>
                                    <span className="font-semibold text-on-surface">Email:</span>{' '}
                                    <span className="text-on-surface-variant">{row.requesterEmail || '-'}</span>
                                  </p>
                                  <div>
                                    <p className="mb-1 font-semibold text-on-surface">Attachments:</p>
                                    {row.attachments && row.attachments.length > 0 ? (
                                      <ul className="space-y-1.5">
                                        {row.attachments.map((att) => (
                                          <li
                                            key={att.id}
                                            className="flex items-center gap-2 rounded-md bg-surface-container-lowest px-3 py-2 ring-1 ring-outline-variant/20"
                                          >
                                            <span className="material-symbols-outlined text-sm text-on-surface-variant">
                                              description
                                            </span>
                                            <span className="flex-1 truncate text-sm text-on-surface">
                                              {att.originalName || 'Attachment'}
                                            </span>
                                            <button
                                              type="button"
                                              onClick={() => actions.previewAttachment(row, att)}
                                              className="inline-flex h-7 w-7 items-center justify-center rounded-full border border-outline-variant/30 text-on-surface-variant hover:bg-surface-container-high hover:text-primary transition-colors cursor-pointer"
                                              title="Preview / Download"
                                            >
                                              <span className="material-symbols-outlined text-sm">visibility</span>
                                            </button>
                                            {canEditStatus ? (
                                              <button
                                                type="button"
                                                onClick={() => actions.deleteAttachment(row, att)}
                                                className="inline-flex h-7 w-7 items-center justify-center rounded-full border border-outline-variant/30 text-on-surface-variant hover:bg-surface-container-high hover:text-error transition-colors cursor-pointer"
                                                title="Delete attachment"
                                              >
                                                <span className="material-symbols-outlined text-sm">delete</span>
                                              </button>
                                            ) : null}
                                          </li>
                                        ))}
                                      </ul>
                                    ) : (
                                      <span className="text-sm text-on-surface-variant">None</span>
                                    )}
                                  </div>
                                </div>
                              </div>

                              {/* Conversation Panel */}
                              <ConversationPanel row={row} state={state} actions={actions} />
                            </div>
                          </td>
                        </tr>
                      ) : null}
                    </Fragment>
                  )
                })
              )}
            </tbody>
          </table>
        </div>

        {/* Pagination */}
        <div className="flex items-center justify-end border-t border-surface-container px-4 py-3">
          <AppPagination
            page={state.page}
            totalPages={state.totalPages}
            onPageChange={actions.setPage}
            onPrevious={() => actions.setPage(state.page - 1)}
            onNext={() => actions.setPage(state.page + 1)}
            canPrevious={state.page > 1}
            canNext={state.page < state.totalPages}
          />
        </div>
      </section>

      {/* Assign Agent Modal */}
      <AssignAgentModal
        isOpen={state.assignModalOpen}
        onClose={actions.closeAssignModal}
        ticket={state.ticketToAssign}
        agents={state.agents}
        onAssign={actions.assignTicket}
        isSaving={state.isAssigning}
      />

      {/* Super Admin Platform Staff Management Modal */}
      {state.isSuperAdmin && (
        <PlatformStaffManagementModal
          isOpen={state.staffModalOpen}
          onClose={actions.closeStaffModal}
          tenants={state.tenants}
          onStaffUpdated={actions.refresh}
        />
      )}
    </div>
  )
}
